import React from 'react';

import type {
  DocumentCategory,
  DocumentRequirement,
  DocumentUploadFile,
} from './documentDefinitions';

type DocumentCategoryCardProps = {
  category: DocumentCategory;
  requirements: DocumentRequirement[];
  filesByRequirement: Record<string, DocumentUploadFile[]>;
  onOpenRequirement: (requirement: DocumentRequirement) => void;
  onPreviewFile: (requirementId: string, file: DocumentUploadFile) => void;
};

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);

function getRowStatus(files: DocumentUploadFile[]) {
  if (files.some((file) => file.status === 'uploading')) {
    return {label: 'In progress', tone: 'progress'} as const;
  }

  if (files.length > 0) {
    return {label: 'Added', tone: 'added'} as const;
  }

  return {label: 'Not added', tone: 'empty'} as const;
}

function getFileExtensionLabel(file: DocumentUploadFile) {
  return String(file.extension || file.name?.split('.').pop() || 'file')
    .trim()
    .toUpperCase()
    .slice(0, 4);
}

function isImageFile(file: DocumentUploadFile) {
  return (
    Boolean(file.url) &&
    IMAGE_EXTENSIONS.has(String(file.extension || '').toLowerCase())
  );
}

const DocumentCategoryCard: React.FC<DocumentCategoryCardProps> = ({
  category,
  requirements,
  filesByRequirement,
  onOpenRequirement,
  onPreviewFile,
}) => {
  return (
    <div className="documents-card">
      <header>
        <h3>{category.name}</h3>
        {category.description ? <p>{category.description}</p> : null}
      </header>

      <div className="documents-card__rows">
        {requirements.map((requirement) => {
          const files = filesByRequirement[requirement.id] || [];
          const uploadedFiles = files.filter(
            (file) => file.status === 'uploaded',
          );
          const status = getRowStatus(files);
          const hasAny = files.length > 0;
          const hasUploaded = uploadedFiles.length > 0;

          return (
            <div key={requirement.id} className="document-row">
              <div
                className={`document-row__title${
                  hasUploaded ? '' : ' document-row__title--empty'
                }`}
              >
                <span>
                  {requirement.name}
                  {requirement.isRequired ? '' : ' (optional)'}
                </span>

                {hasUploaded ? (
                  <div
                    className="document-row__thumbs"
                    aria-label={`${uploadedFiles.length} uploaded file(s)`}
                  >
                    {uploadedFiles.map((file) =>
                      file.url ? (
                        <button
                          key={file.uid}
                          type="button"
                          className="document-row__thumb document-row__thumb-link"
                          onClick={() => onPreviewFile(requirement.id, file)}
                          title={`Preview ${file.name}`}
                          aria-label={`Preview ${file.name}`}
                        >
                          {isImageFile(file) ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              loading="lazy"
                            />
                          ) : (
                            <span>{getFileExtensionLabel(file)}</span>
                          )}
                        </button>
                      ) : (
                        <div
                          key={file.uid}
                          className="document-row__thumb"
                          title={file.name}
                        >
                          <span>{getFileExtensionLabel(file)}</span>
                        </div>
                      ),
                    )}
                  </div>
                ) : null}
              </div>

              <div
                className={`document-row__status document-row__status--${status.tone}`}
              >
                {status.label}
              </div>

              <div className="document-row__action">
                <button
                  type="button"
                  className={`money-btn money-btn--${
                    hasAny ? 'secondary' : 'primary'
                  } money-btn--sm document-row__manage-btn`}
                  onClick={() => onOpenRequirement(requirement)}
                >
                  {hasAny ? 'Upload more' : 'Upload first'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocumentCategoryCard;
